import React, { useEffect, useRef, useState } from 'react';

// Check if mobile device
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768;
};

// Smooth Section Wrapper Component (Optimized for Mobile)
export const SmoothSection = ({ 
  children, 
  className = '', 
  animation = 'fade-up', // fade-up, blur, scale, clip
  delay = 0,
  blendTop = false,
  blendBottom = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    if (!currentRef) return;

    // On mobile, trigger animation earlier and with simpler threshold
    const mobileDevice = isMobile();
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
          observer.unobserve(currentRef);
        }
      },
      { 
        threshold: mobileDevice ? 0.01 : 0.05,
        rootMargin: mobileDevice ? '100px 0px' : '50px 0px'
      }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const animationClass = {
    'fade-up': 'section-smooth',
    'blur': 'blur-transition',
    'scale': 'scale-reveal',
    'clip': 'clip-reveal'
  }[animation] || 'section-smooth';

  const blendClasses = `${blendTop ? 'section-blend-top' : ''} ${blendBottom ? 'section-blend-bottom' : ''}`;

  // Reduce delay on mobile
  const mobileDelay = isMobile() ? Math.min(delay, 50) : delay;

  return (
    <div
      ref={sectionRef}
      className={`${animationClass} ${isVisible ? 'visible' : ''} ${blendClasses} ${className}`}
      style={{ transitionDelay: `${mobileDelay}ms` }}
    >
      {children}
    </div>
  );
};

// Staggered Content Wrapper
export const StaggerContent = ({ children, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(currentRef);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`content-stagger ${isVisible ? 'visible' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

export default SmoothSection;
